import * as analytic from '@anm/analytic';
import { VideoSizes } from '@anm/hosting-player';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { hostedVideoSelectors, EmbedData } from '../../../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../../../store/modules/project';
import { userSelectors } from '../../../../../store/modules/user';

import { formatSnippet, generateSnippet } from './helpers';
import { EMBED_VERTICAL_WIDTH, EMBED_HORIZONTAL_WIDTH } from '@anm/constants/embed';
import useIsWebinar from '../../../../../hooks/useIsWebinar';

type EmbedLimitProps = {
  embedLimit?: number;
  embeddedCount?: number;
};

export const useEmbedCodeSnippet = () => {
  const embedData = useSelector(hostedVideoSelectors.selectEmbedData);
  const embedSizes = useEmbedSizes(embedData?.sizes);
  const isWebinar = useIsWebinar();

  const dataWithSizes: EmbedData = {
    ...embedData!,
    isWebinar,
    sizes: embedSizes
  };

  const formattedSnippet = formatSnippet(generateSnippet(dataWithSizes) || '');

  return [dataWithSizes, formattedSnippet] as const;
};

const { selectUserEmbeds, selectUserExtraEmbeds } = userSelectors;

export const useEmbedLimit = ({ embedLimit, embeddedCount }: EmbedLimitProps) => {
  const [isLimitUsed, setIsLimitUsed] = useState(false);

  useEffect(() => {
    if (!embeddedCount || !embedLimit) return;

    const canSetLimit = embeddedCount >= embedLimit;
    const canUnsetLimit = isLimitUsed && !canSetLimit;

    canSetLimit && setIsLimitUsed(true);
    canUnsetLimit && setIsLimitUsed(false);
  }, [embeddedCount]);

  return isLimitUsed;
};

export const useEmbeddedVideos = () => {
  const { available: embedLimit, used: embeddedCount, state } = useSelector(selectUserEmbeds) || {};
  const { extraEmbedPrice, canPurchaseEmbeds = false } = useSelector(selectUserExtraEmbeds) || {};

  const canPay = state === 'enabled';

  useEffect(() => {
    analytic.trackVideosEmbedSettingsOpen();
  }, []);

  return {
    canPay,
    embedLimit,
    embeddedCount,
    canPurchaseEmbeds,
    extraEmbedPrice
  };
};

export const useEmbedSizes = (sizes?: Partial<VideoSizes>) => {
  const projectSizes = useSelector(projectSelectors.selectCurrentProjectSizes) as VideoSizes;

  if (sizes?.width && sizes?.height) return sizes as VideoSizes;

  const ratio = projectSizes.width / projectSizes.height;
  const width = ratio >= 1 ? EMBED_HORIZONTAL_WIDTH : EMBED_VERTICAL_WIDTH;
  const height = Math.round(width / ratio);

  return { width, height };
};
